import { DAY_NAMES } from "@/lib/collectionCoverage"
import { AvailabilitySlot, DayOfWeek, SlotPnl, SlotService } from "@/types/collections"

/**
 * Turning a Mon-Sun grid back into availability slot rows.
 *
 * The grid the planner edits is per-day. A slot row covers a group of days that share an agreed
 * arrival and a dwell, so saving has to regroup, and regrouping naively -- delete everything for
 * the site, insert the new set -- would throw away `confirmed_at`, `source` and `service_time_source`
 * on every row. Those are the record of who checked a window against the shipper agreement and
 * where it came from, and losing them silently turns the exceptions view back off.
 *
 * So each day cell remembers the row it was loaded from. A group of cells that still agree with
 * that row is left alone entirely; one whose time or dwell has moved is an edit; one with no row
 * behind it is new.
 */

/** Monday-first, as the plan is read. An alias of `DAY_NAMES`, not a second copy. */
export const DAY_ORDER: DayOfWeek[] = DAY_NAMES

/**
 * Minutes either side of the agreed arrival that still count as turning up on time. A policy
 * choice rather than anything a shipper states, and it must stay in step with the tolerance the
 * seed used -- see 20260902160000_collections_slot_provenance.sql.
 */
export const ARRIVAL_GRACE_MINUTES = 30

export interface DayCell {
  enabled: boolean
  /** Agreed arrival, HH:mm. */
  time: string
  /** Minutes on site. */
  minutes: number
  /** The slot row this cell was loaded from, if any. */
  slotId?: string
}

/**
 * What the planner needs from an agreement beyond its days and times. Held per line, because a
 * line is one agreement: its rows are edited together and written together. Null is unknown or
 * the planner's default in every field but priority, which the column never lets be null.
 */
export interface SlotPlanning {
  injection_depot_id: string | null
  allowed_vehicle_types: string[] | null
  expected_units: number | null
  priority: number
  miss_penalty_gbp: number | null
  excluded_dates: string[] | null
}

/** A row's planning fields, with an empty list read as null so the two never count as a change. */
export const planningOf = (slot: Partial<AvailabilitySlot>): SlotPlanning => ({
  injection_depot_id: slot.injection_depot_id ?? null,
  allowed_vehicle_types: slot.allowed_vehicle_types?.length
    ? [...slot.allowed_vehicle_types].sort()
    : null,
  expected_units: slot.expected_units != null ? Number(slot.expected_units) : null,
  priority: slot.priority ?? 0,
  miss_penalty_gbp: slot.miss_penalty_gbp != null ? Number(slot.miss_penalty_gbp) : null,
  excluded_dates: slot.excluded_dates?.length
    ? slot.excluded_dates.map((d) => d.slice(0, 10)).sort()
    : null,
})

const planningKey = (slot: Partial<AvailabilitySlot>): string => JSON.stringify(planningOf(slot))

export interface ServiceLine {
  services: SlotService[]
  pnl: SlotPnl[]
  /**
   * Whether this collection is running. Separate from the day ticks on purpose: a shipper that
   * has not gone live yet has agreed days and times, and untangling the two is what stops a save
   * from deleting the agreement along with the pause.
   */
  isActive: boolean
  days: Partial<Record<DayOfWeek, DayCell>>
  /** Left unset, the rows' planning fields are not written at all. */
  planning?: SlotPlanning
}

export interface SlotWrite {
  agreed_arrival: string
  start_time: string
  end_time: string
  service_time_minutes: number
  days: DayOfWeek[]
  slot_services: SlotService[]
  slot_pnl: SlotPnl[]
  slot_type: "shipper_collection"
  is_active: boolean
  /** Only set when the planner has stated the time here, so untouched rows keep their provenance. */
  source?: "manual"
  confirmed_at?: string
  service_time_source?: "manual"
  injection_depot_id?: string | null
  allowed_vehicle_types?: string[] | null
  expected_units?: number | null
  priority?: number
  miss_penalty_gbp?: number | null
  excluded_dates?: string[] | null
}

export interface SlotPlan {
  creates: SlotWrite[]
  updates: Array<{ id: string; data: Partial<SlotWrite> }>
  deletes: string[]
}

const toMinutes = (time: string): number => {
  const [h, m] = time.split(":").map(Number)
  return h * 60 + m
}

const toTime = (mins: number): string => {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, mins))
  return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`
}

/** HH:mm from an HH:mm or HH:mm:ss stored value. */
export const trimTime = (time: string): string => time.slice(0, 5)

/**
 * The occupancy window around an agreed arrival: from the arrival less the grace, through to the
 * arrival plus the dwell plus the grace. Clamped at 23:59, because a time column cannot express a
 * window that crosses midnight -- a 23:30 arrival with an hour on site is the case that hits it.
 */
export const windowFor = (agreed: string, minutes: number) => ({
  start_time: toTime(toMinutes(agreed) - ARRIVAL_GRACE_MINUTES),
  end_time: toTime(toMinutes(agreed) + minutes + ARRIVAL_GRACE_MINUTES),
})

/** What a stored row means in grid terms: its arrival, falling back to the window start. */
export const agreedArrivalOf = (slot: AvailabilitySlot): string =>
  trimTime(slot.agreed_arrival ?? slot.start_time)

const sameSet = <T,>(a: T[], b: T[]): boolean =>
  a.length === b.length && [...a].sort().every((v, i) => v === [...b].sort()[i])

const orderDays = (days: DayOfWeek[]): DayOfWeek[] =>
  DAY_ORDER.filter((d) => days.includes(d))

/**
 * Expand a location's stored rows into the per-service lines the grid edits. Rows are grouped by
 * their service set, because that is what a planner thinks of as one collection: a site on a
 * service split has one line for SD and another for ND/2D, each with its own days and times.
 * Live and paused rows for the same service stay apart, so a site waiting to go live keeps its
 * agreed days visible rather than reading as a collection nobody has set up. So do rows whose
 * planning fields differ: shown as one line they would be saved as one, overwriting whichever
 * row was not the one displayed.
 */
export function toServiceLines(slots: AvailabilitySlot[]): ServiceLine[] {
  const byServices = new Map<string, ServiceLine>()

  for (const slot of slots) {
    if (slot.slot_type !== "shipper_collection") continue
    const services = [...(slot.slot_services ?? [])].sort()
    const planning = planningOf(slot)
    const key = `${services.join("+") || "none"}|${slot.is_active}|${planningKey(planning)}`

    let line = byServices.get(key)
    if (!line) {
      line = {
        services: services as SlotService[],
        pnl: slot.slot_pnl ?? [],
        isActive: slot.is_active,
        days: {},
        planning,
      }
      byServices.set(key, line)
    }

    for (const day of slot.days ?? []) {
      line.days[day] = {
        enabled: true,
        time: agreedArrivalOf(slot),
        minutes: slot.service_time_minutes,
        slotId: slot.id,
      }
    }
  }

  return [...byServices.values()]
}

interface Group {
  time: string
  minutes: number
  days: DayOfWeek[]
  slotId?: string
}

/** Days sharing an arrival and a dwell become one row. */
function groupLine(line: ServiceLine): Group[] {
  const groups = new Map<string, Group>()

  for (const day of DAY_ORDER) {
    const cell = line.days[day]
    if (!cell?.enabled || !cell.time) continue
    const key = `${cell.time}|${cell.minutes}`
    const group = groups.get(key)
    if (group) {
      group.days.push(day)
    } else {
      groups.set(key, { time: cell.time, minutes: cell.minutes, days: [day] })
    }
  }

  // Give each group the row most of its days came from, so an unchanged group keeps its identity
  // and a group that has absorbed a day from elsewhere still edits the row it mostly is.
  for (const group of groups.values()) {
    const votes = new Map<string, number>()
    for (const day of group.days) {
      const id = line.days[day]?.slotId
      if (id) votes.set(id, (votes.get(id) ?? 0) + 1)
    }
    let best: string | undefined
    let bestCount = 0
    for (const [id, count] of votes) {
      if (count > bestCount) {
        best = id
        bestCount = count
      }
    }
    group.slotId = best
  }

  return [...groups.values()]
}

/**
 * Work out the writes that turn the stored rows for one location into what the grid now shows.
 *
 * `now` is passed in rather than read, so the caller decides the confirmation timestamp and the
 * function stays testable.
 */
export function reconcileSlots(
  existing: AvailabilitySlot[],
  lines: ServiceLine[],
  now: string,
): SlotPlan {
  const editable = existing.filter((s) => s.slot_type === "shipper_collection")
  const plan: SlotPlan = { creates: [], updates: [], deletes: [] }
  const claimed = new Set<string>()

  for (const line of lines) {
    // Groups that still agree with the row they came from go first, so they keep it. Otherwise a
    // single day moved to a new time can claim the row out from under the days that did not move,
    // which would hand the moved day the old row's provenance and mint a fresh one for the days
    // that were never touched -- exactly backwards.
    const groups = groupLine(line).sort((a, b) => {
      const settled = (g: Group) => {
        const row = g.slotId ? editable.find((s) => s.id === g.slotId) : undefined
        return row && agreedArrivalOf(row) === g.time && row.service_time_minutes === g.minutes
          ? 0
          : 1
      }
      return settled(a) - settled(b)
    })

    for (const group of groups) {
      const days = orderDays(group.days)
      const window = windowFor(group.time, group.minutes)
      const base = {
        agreed_arrival: group.time,
        ...window,
        service_time_minutes: group.minutes,
        days,
        slot_services: line.services,
        slot_pnl: line.pnl,
        slot_type: "shipper_collection" as const,
        is_active: line.isActive,
        ...(line.planning ? planningOf(line.planning) : {}),
      }

      const previous =
        group.slotId && !claimed.has(group.slotId)
          ? editable.find((s) => s.id === group.slotId)
          : undefined

      if (!previous) {
        // Nothing behind it: the planner has stated this window, so it is confirmed on the spot.
        plan.creates.push({
          ...base,
          source: "manual",
          service_time_source: "manual",
          confirmed_at: now,
        })
        continue
      }

      claimed.add(previous.id)

      const timeMoved =
        agreedArrivalOf(previous) !== group.time ||
        previous.service_time_minutes !== group.minutes
      const daysMoved = !sameSet(previous.days ?? [], days)
      const servicesMoved = !sameSet(previous.slot_services ?? [], line.services)
      const pnlMoved = !sameSet(previous.slot_pnl ?? [], line.pnl)
      const activeMoved = previous.is_active !== line.isActive
      const planningMoved =
        line.planning !== undefined && planningKey(previous) !== planningKey(line.planning)

      const moved =
        timeMoved || daysMoved || servicesMoved || pnlMoved || activeMoved || planningMoved
      if (!moved) continue

      plan.updates.push({
        id: previous.id,
        data: {
          ...base,
          // Only a moved window is a new statement about the agreement. Adding a day, or fixing a
          // service code, leaves the agreed time exactly as whoever set it left it.
          ...(timeMoved
            ? { source: "manual" as const, service_time_source: "manual" as const, confirmed_at: now }
            : {}),
        },
      })
    }
  }

  for (const slot of editable) {
    if (!claimed.has(slot.id)) plan.deletes.push(slot.id)
  }

  return plan
}
